import type { FC, ReactNode } from "react";
import { forwardRef } from "react";
import {
  Button as ReactAriaButton,
  Popover as ReactAriaPopover,
  Select as ReactAriaSelect,
  SelectValue as ReactAriaSelectValue,
  type Key,
  type SelectProps as ReactAriaSelectProps,
} from "react-aria-components";
import { FieldError } from "react-hook-form";

import { cn, type VariantProps } from "../utils";
import { NcFieldLayout, NcFieldLayoutProps } from "./nc-field-layout";
import { NcFormattedMessage } from "./nc-formatted-message";
import { NcIconChevronDown } from "./nc-icons";
import { ncInputWidthVariants } from "./nc-input";
import { NcLabel } from "./nc-label";
import { NcListBox } from "./nc-list-box";
import { ncPopoverClasses } from "./nc-popover";

export type NcSelectItem = {
  id: Key;
  label: string;
  description?: string;
  Icon?: FC;
} & Record<string, Key | FC>;

export interface NcSelectProps
  extends VariantProps<typeof ncInputWidthVariants>, ReactAriaSelectProps<NcSelectItem> {
  label: string;
  hideLabel?: boolean;
  description?: string;
  variant?: NcFieldLayoutProps["variant"];
  items?: Iterable<NcSelectItem>;
  renderEmptyState?: () => ReactNode;
  error?: FieldError;
}

const BaseItem = ({ item }: { item: NcSelectItem }) => {
  const { Icon, label } = item;
  return (
    <div className="flex items-center gap-2">
      {Icon && <Icon />}
      <span>{label}</span>
    </div>
  );
};

export const NcSelect = forwardRef<HTMLButtonElement, NcSelectProps>(
  (
    {
      label,
      hideLabel,
      description,
      items,
      name,
      renderEmptyState,
      inputWidth = "auto",
      variant,
      isRequired,
      error,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <ReactAriaSelect
        data-nc="NcFieldSelect"
        className={cn("relative data-focus-visible:outline-none!", className)}
        {...props}
      >
        <NcFieldLayout variant={variant}>
          <NcLabel htmlFor={name} isRequired={isRequired} hideLabel={hideLabel}>
            {label}
          </NcLabel>
          <ReactAriaButton
            name={name}
            ref={ref}
            className={cn(
              "text-dark bg-light border-ui focus:border-active placeholder-ui-dark inline-flex w-full max-w-full items-center justify-between gap-2 rounded-md",
              "disabled:bg-disabled-light data-invalid:border-danger border px-3 py-2 text-left transition-colors duration-300 disabled:cursor-not-allowed",
              "data-hovered:bg-active data-hovered:text-light data-hovered:placeholder-light",
              "data-focused:bg-light data-focused:text-dark data-focused:placeholder-dark",
              "data-pressed:bg-light data-pressed:text-dark data-pressed:placeholder-dark",
              "data-focused:outline-main data-focused:outline-2 data-focused:outline-offset-2",
              ncInputWidthVariants({ inputWidth })
            )}
          >
            <ReactAriaSelectValue>
              {({ selectedItems, defaultChildren }) => {
                if (selectedItems && selectedItems[0]) {
                  return <BaseItem item={selectedItems[0] as NcSelectItem} />;
                }
                return defaultChildren;
              }}
            </ReactAriaSelectValue>
            <NcIconChevronDown aria-hidden="true" />
          </ReactAriaButton>
          <ReactAriaPopover
            className={cn(ncPopoverClasses.popover, "min-w-(--trigger-width) overflow-y-auto")}
          >
            <NcListBox items={items} renderEmptyState={renderEmptyState}>
              {(item: NcSelectItem) => {
                const { id, label, description } = item;
                return (
                  <NcListBox.Item id={id} textValue={label}>
                    <div className="data-focus-visible:outline-none">
                      <BaseItem item={item} />
                      {description && (
                        <NcFormattedMessage variant="secondary" slot="description">
                          {description}
                        </NcFormattedMessage>
                      )}
                    </div>
                  </NcListBox.Item>
                );
              }}
            </NcListBox>
          </ReactAriaPopover>
          {description && <NcFieldLayout.Description>{description}</NcFieldLayout.Description>}
          <NcFieldLayout.ErrorMessage>{error?.message}</NcFieldLayout.ErrorMessage>
        </NcFieldLayout>
      </ReactAriaSelect>
    );
  }
);
